/**
 * Canonical product-facing metric nouns.
 *
 * Keep UI copy on these — never invent synonyms per-surface.
 *
 * - views (total views)    = pageviews (hits)
 * - visitors (unique visitors) = unique sessions/people (a session ends after ~30m inactivity)
 *
 * Funnels count unique visitors, not page views.
 */

export const METRIC = {
  view: { singular: "view", plural: "views" },
  visitor: { singular: "visitor", plural: "visitors" },
} as const

/** Pluralize a metric noun for a count. */
function metricNoun(
  kind: keyof typeof METRIC,
  count: number,
): string {
  return count === 1 ? METRIC[kind].singular : METRIC[kind].plural
}

/** Short funnel unit clarifier (chip / helper). */
export const FUNNEL_COUNT_LABEL = "Counts = unique visitors"

export const FUNNEL_COUNT_TOOLTIP =
  "Every number is a unique visitor. A visitor's session ends after ~30 min of inactivity, so the same person returning later counts again. Not page views."

/**
 * Forms-page only. Elsewhere the deduped noun stays "unique visitors" — this
 * wording exists because people compare the forms number against CRM leads.
 */
export const UNIQUE_SUBMISSIONS_TOOLTIP =
  "Each person counted once, no matter how many times they submitted. This is the number to compare against leads in your CRM. Submissions counts every submit event, so it runs higher."

/** Explains why a form's conversion rate can read above 100%. */
export const FORM_CONV_RATE_TOOLTIP =
  "Unique Submissions ÷ Unique Visitors for the period: the share of visitors who submitted a form. Each person counts once, however many times they submit."

/**
 * CTR on Clicks / Pages = total clicks ÷ unique visitors (sessions).
 * One person can click many times, so the rate can exceed 100%.
 */
export const CTR_TOOLTIP =
  "Clicks ÷ Unique Visitors. Clicks counts every click event and Unique Visitors counts each person once, so 2.00 means visitors clicked twice each on average. It is a count per visitor, not a rate, so it can pass 1."

/** Shorter label companion when the column header is just "CTR". */
const CTR_LABEL = "Click-through rate"
